/** Retour navigateur / geste du hub : un cran dans Factures, pas la sortie vers le bureau. */
import { useEffect, useRef } from "react";

type Layer = { id: number; run: () => void };

const layers: Layer[] = [];
let seq = 1;
let mute = 0;
let installed = false;

function reply(event: MessageEvent, id: number, handled: boolean) {
  const src = event.source;
  if (!src || typeof (src as Window).postMessage !== "function") return;
  (src as Window).postMessage({ type: "aurelia:back", id, handled }, "*");
}

function onPop() {
  if (mute > 0) {
    mute -= 1;
    return;
  }
  const top = layers[layers.length - 1];
  if (!top || history.state?.fxBack === top.id) return;
  layers.pop();
  top.run();
}

function onMessage(event: MessageEvent) {
  const data = event.data as { type?: string; id?: number } | null;
  if (!data || data.type !== "aurelia:back" || typeof data.id !== "number") return;
  if (event.source !== window.parent || window.parent === window) return;
  const top = layers[layers.length - 1];
  if (!top) {
    reply(event, data.id, false);
    return;
  }
  reply(event, data.id, true);
  if (history.state?.fxBack === top.id) {
    mute += 1;
    history.back();
  }
  const at = layers.findIndex((layer) => layer.id === top.id);
  if (at >= 0) layers.splice(at, 1);
  top.run();
}

export function installHubBack() {
  if (installed || typeof window === "undefined") return;
  installed = true;
  window.addEventListener("popstate", onPop);
  window.addEventListener("message", onMessage);
}

/** `depth` 0 = racine de la page. Chaque cran au-dessus est un retour (fiche, facture, modale). */
export function useHubBack(depth: number, onBack: () => void) {
  const onBackRef = useRef(onBack);
  onBackRef.current = onBack;
  const owned = useRef<number[]>([]);
  const depthRef = useRef(0);

  useEffect(() => {
    installHubBack();
    return () => {
      for (const id of owned.current) {
        const at = layers.findIndex((layer) => layer.id === id);
        if (at >= 0) layers.splice(at, 1);
      }
      owned.current = [];
    };
  }, []);

  useEffect(() => {
    const prev = depthRef.current;
    if (depth > prev) {
      for (let i = prev; i < depth; i += 1) {
        const id = seq++;
        owned.current.push(id);
        layers.push({ id, run: () => onBackRef.current() });
        history.pushState({ fxBack: id }, "");
      }
    } else if (depth < prev) {
      const extra = owned.current.length - depth;
      for (let n = 0; n < extra; n += 1) {
        const id = owned.current[owned.current.length - 1];
        const at = layers.findIndex((layer) => layer.id === id);
        if (id != null && history.state?.fxBack === id && at >= 0) {
          layers.splice(at, 1);
          owned.current.pop();
          mute += 1;
          history.back();
        } else {
          if (at >= 0) layers.splice(at, 1);
          owned.current.pop();
        }
      }
    }
    depthRef.current = depth;
  }, [depth]);
}
